// Heuristic byline + affiliation extraction.
//
// Strategy: look at the region between the detected title and the abstract
// (or the first 2000 chars if no abstract is found). Extract:
//   - Author *surnames* (the last capitalized word of each name token)
//   - Affiliation strings (lines containing institution keywords)
//
// This is a best-effort heuristic. It's used to seed the COI filter for
// reviewer search; an editor can override in Sprint 6 polish if it's wrong.

import type { ManuscriptDocument } from './types';

const INSTITUTION_KEYWORDS = [
  'university',
  'hospital',
  'institute',
  'department',
  'school of',
  'faculty of',
  'college',
  'centre',
  'center',
  'clinic',
  'foundation',
  'laboratory',
  'laboratorio',
  'unidade',
  'instituto',
  'universidad',
];

// Inline-only separator (space, tab, period, hyphen) — newlines act as boundaries
// so we don't accidentally fuse "Mary Brown\nDepartment …" into one name token.
const NAME_TOKEN_RE =
  /\b(?:[A-Z][a-z]+|[A-Z]\.?)(?:[ \t.-]+(?:[A-Z][a-z]+|[A-Z]\.?))+(?:[ \t]+[A-Z][a-z]+)?/g;

const NAME_BLOCKLIST = new Set([
  'abstract',
  'background',
  'introduction',
  'methods',
  'methodology',
  'materials',
  'results',
  'findings',
  'discussion',
  'conclusion',
  'conclusions',
  'references',
  'acknowledgements',
  'acknowledgments',
  'funding',
  'manuscript',
  'article',
  'research',
  'study',
  'authors',
  'author',
  'editor',
  'editorial',
  'received',
  'accepted',
  'published',
  'copyright',
  'preprint',
  'corresponding',
]);

function bylineRegion(doc: ManuscriptDocument): string {
  if (doc.title && doc.rawText.startsWith(doc.title)) {
    const start = doc.title.length;
    const abstractSpan = doc.sections.find((s) => s.section === 'abstract');
    const end = abstractSpan ? abstractSpan.start : Math.min(doc.rawText.length, start + 2000);
    return doc.rawText.slice(start, end);
  }
  const abstractSpan = doc.sections.find((s) => s.section === 'abstract');
  const end = abstractSpan ? abstractSpan.start : Math.min(doc.rawText.length, 2000);
  return doc.rawText.slice(0, end);
}

function lastSurname(name: string): string | null {
  // Strip superscript-like trailing digits/markers and punctuation.
  const cleaned = name.replace(/[\d*†‡§¶]+$/g, '').trim();
  const parts = cleaned.split(/[\s.-]+/).filter(Boolean);
  if (parts.length === 0) return null;
  // Skip pure initials like "J" — keep last token that's at least 2 chars and lowercase-y.
  for (let i = parts.length - 1; i >= 0; i--) {
    const p = parts[i];
    if (p.length >= 3 && /^[A-Z][a-z]+$/.test(p)) return p.toLowerCase();
  }
  return null;
}

export function extractAuthorSurnames(doc: ManuscriptDocument): string[] {
  const region = bylineRegion(doc);
  if (region.trim().length === 0) return [];

  const found = new Set<string>();
  NAME_TOKEN_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = NAME_TOKEN_RE.exec(region)) !== null) {
    const surname = lastSurname(m[0]);
    if (!surname) continue;
    if (NAME_BLOCKLIST.has(surname)) continue;
    found.add(surname);
    if (found.size >= 30) break;
  }
  return [...found];
}

export function extractAffiliations(doc: ManuscriptDocument): string[] {
  const region = bylineRegion(doc);
  if (region.trim().length === 0) return [];

  const out: string[] = [];
  const seen = new Set<string>();
  for (const line of region.split('\n')) {
    const trimmed = line.trim();
    if (trimmed.length < 8 || trimmed.length > 300) continue;
    const lower = trimmed.toLowerCase();
    if (!INSTITUTION_KEYWORDS.some((kw) => lower.includes(kw))) continue;
    // Strip leading superscript markers and footnote numbers.
    const cleaned = trimmed.replace(/^[\d*†‡§¶\s.,]+/, '').trim();
    if (cleaned.length < 8) continue;
    const key = cleaned.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(cleaned);
    if (out.length >= 10) break;
  }
  return out;
}
